/**
 * Job Collection Utilities
 *
 * Functions for manipulating collections of jobs (deduplication, merging, etc.)
 */

import { Job } from '@/types';

/**
 * Deduplicates an array of jobs by key
 *
 * When searching across multiple paths (name, DNS, source),
 * the same job may appear in multiple results. This function
 * deduplicates based on job.key.
 *
 * @param jobs - Array of jobs that may contain duplicates
 * @returns Array with duplicate jobs removed (keeps first occurrence)
 *
 * @example
 * const unique = deduplicateJobs([job1, job1, job2]);
 * // Returns: [job1, job2]
 */
export const deduplicateJobs = (jobs: Job[]): Job[] => {
  return jobs.reduce((acc, job) => {
    if (!acc.find(a => a.key === job.key)) {
      acc.push(job);
    }
    return acc;
  }, [] as Job[]);
};

/**
 * Merges multiple job arrays and removes duplicates
 *
 * Flattens multiple arrays of jobs and deduplicates by key.
 * Useful for combining results from multiple API queries.
 *
 * @param jobArrays - Variable number of job arrays (undefined allowed)
 * @returns Single deduplicated array
 *
 * @example
 * const merged = mergeJobArrays(
 *   searchByName?.jobs,
 *   searchByDns?.jobs,
 *   searchBySource?.jobs
 * );
 */
export const mergeJobArrays = (...jobArrays: (Job[] | undefined)[]): Job[] => {
  const flattened = jobArrays
    .filter((arr): arr is Job[] => arr !== undefined)
    .flat();

  return deduplicateJobs(flattened);
};
